import type { KaiMessageMetadata } from "@/features/kai/kaiTypes";

/** File types accepted by the Kai document extraction pipeline. */
export const KAI_ACCEPT = ".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp,.tiff";

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
  "tiff",
  "tif",
]);

/** One attachment referenced from a persisted user message, in display order. */
export interface MessageAttachmentRef {
  id: string;
  filename: string;
  isImage: boolean;
}

/** Infer whether a filename points at an image the UI can thumbnail. */
export function isImageFilename(filename: string): boolean {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return IMAGE_EXTENSIONS.has(ext);
}

/** PDF / image types the Kai download artifacts can preview in-app. */
export function isInlinePreviewableFilename(filename: string): boolean {
  return /\.(pdf|png|jpe?g|gif|webp|avif|bmp)$/i.test(filename);
}

/** Parse ordered attachment refs from a message metadata blob. */
export function parseMessageAttachments(
  metadata: KaiMessageMetadata | null | undefined,
): MessageAttachmentRef[] {
  if (!metadata?.attachment_ids?.length) return [];

  const filenames = metadata.filenames ?? [];
  return metadata.attachment_ids.map((id, index) => {
    const filename = filenames[index] ?? "Attachment";
    return {
      id,
      filename,
      isImage: isImageFilename(filename),
    };
  });
}

/** Stable identity for a browser `File` (refs change when re-wrapped). */
export function fileFingerprint(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

/** Build the user-visible bubble text for a turn that may include files only. */
export function buildUserDisplayContent(
  content: string,
  filenames: string[],
): string {
  if (content.trim()) return content;
  if (filenames.length === 0) return "";
  return `Uploaded ${filenames.join(", ")}`;
}
